import React from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Stack,
    Link,
    useTheme,
    IconButton,
    Divider,
    Paper,
    alpha, // <--- Moved alpha here
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/PhoneRounded';
import EmailIcon from '@mui/icons-material/EmailRounded';
import LocationOnIcon from '@mui/icons-material/LocationOnRounded';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnquirySection from '../components/EnquirySection';

interface ContactUsPageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

const ContactUsPage = ({ mode, setMode }: ContactUsPageProps) => {
    const theme = useTheme();

    // Contact Information
    const contactInfo = {
        email: 'hello@workhorse.com.sg',
        phone: '+65 9837 8090',
        whatsapp: '+65 9837 8090',
        address: 'A-6-13, The Tube, Jalan PJU 1/39, Dataran Prima, 47301 Petaling Jaya, Selangor',
        facebook: 'https://www.facebook.com/profile.php?viewas=100000686899395&id=61584101914147',
        instagram: 'https://www.instagram.com/sg.workhorse/',
    };

    // Helper for Clean Contact Rows
    const ContactRow = ({
        icon,
        label,
        value,
        href,
        isExternal = false
    }: {
        icon: React.ReactNode,
        label: string,
        value: string,
        href?: string,
        isExternal?: boolean
    }) => (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Box sx={{
                color: 'primary.main',
                bgcolor: theme.palette.mode === 'light' ? 'primary.50' : 'rgba(255,255,255,0.05)',
                p: 1.5,
                borderRadius: '12px',
                height: 'fit-content'
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
                    {label}
                </Typography>
                {href ? (
                    <Link
                        href={href}
                        target={isExternal ? "_blank" : undefined}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                        color="text.primary"
                        underline="hover"
                        sx={{ fontSize: '1.1rem', fontWeight: 500, display: 'block', mt: 0.5 }}
                    >
                        {value}
                    </Link>
                ) : (
                    <Typography color="text.primary" sx={{ fontSize: '1rem', fontWeight: 500, mt: 0.5, lineHeight: 1.6 }}>
                        {value}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    // Fixed Google Maps Embed URL
    const mapEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

    return (
        <>
            <Header />

            {/* --- HERO SECTION --- */}
            <Box sx={{
                bgcolor: theme.palette.mode === 'light' ? '#F4F6F8' : 'background.paper',
                pt: { xs: 12, md: 16 },
                pb: { xs: 8, md: 10 }
            }}>
                <Container maxWidth="lg">
                    <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                        Get in Touch
                    </Typography>
                    <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 600, fontWeight: 400 }}>
                        Have a question or want to work together? We'd love to hear from you.
                    </Typography>
                </Container>
            </Box>

            {/* --- CONTENT SECTION --- */}
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Grid container spacing={8}>

                    {/* LEFT COLUMN: Contact Details */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Stack spacing={4}>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                                    Contact Information
                                </Typography>

                                <ContactRow
                                    icon={<EmailIcon />}
                                    label="Email"
                                    value={contactInfo.email}
                                    href={`mailto:${contactInfo.email}`}
                                />

                                <ContactRow
                                    icon={<PhoneIcon />}
                                    label="Phone"
                                    value={contactInfo.phone}
                                    href={`tel:${contactInfo.phone}`}
                                />

                                <ContactRow
                                    icon={<WhatsAppIcon />}
                                    label="WhatsApp"
                                    value="Chat on WhatsApp"
                                    href={`https://wa.me/${contactInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                                    isExternal
                                />

                                <ContactRow
                                    icon={<LocationOnIcon />}
                                    label="Office"
                                    value={contactInfo.address}
                                />
                            </Box>

                            <Divider />

                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Follow Us
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <IconButton
                                        component="a"
                                        href={contactInfo.facebook}
                                        target="_blank"
                                        sx={{ color: '#1877F2', bgcolor: alpha('#1877F2', 0.1) }}
                                    >
                                        <FacebookIcon />
                                    </IconButton>
                                    <IconButton
                                        component="a"
                                        href={contactInfo.instagram}
                                        target="_blank"
                                        sx={{ color: '#E4405F', bgcolor: alpha('#E4405F', 0.1) }}
                                    >
                                        <InstagramIcon />
                                    </IconButton>
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* RIGHT COLUMN: Map */}
                    {/* <Grid size={{ xs: 12, md: 7 }}>
                        <Paper 
                            elevation={0}
                            sx={{ 
                                height: '100%', 
                                minHeight: { xs: 300, md: 500 }, 
                                borderRadius: 4, 
                                overflow: 'hidden',
                                border: `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <iframe
                                title="WorkHorse Location Map"
                                width="100%"
                                height="100%"
                                style={{ border: 0, minHeight: 500 }}
                                loading="lazy"
                                allowFullScreen
                                src={mapEmbedUrl}
                            />
                        </Paper>
                    </Grid> */}

                    {/* -----------------------------------------------------------------
    2. NEW REPLACEMENT (Contact Image / Support Visual)
    ----------------------------------------------------------------- */}
                    <Grid size={{ xs: 12, md: 7 }}>
  
                            {/* Placeholder image related to 'Contact Us' (Customer Service/Office) */}
                                 <EnquirySection />
                    </Grid>
                </Grid>
            </Container>

            <Footer />
        </>
    );
};

export default ContactUsPage;